/**
 * router/render.ts —— 异步渲染：skeleton → await load → html → mounted
 * 出处：06_二期技术方案.md §三之五 风险①
 *
 * ## 这个文件替一期解决了三件一期没有的事
 * 1. **加载态**（一期明确不做）：首次进页给骨架，页内刷新给顶部进度条。
 * 2. **错误态**：load() 抛出的一切在这里统一变成错误屏 + 重试按钮，页面里一行 try/catch 都不用写。
 * 3. **竞态**：快速连点侧栏时，先发的请求可能后回来，把后一页的内容覆盖掉。
 *    这里用「序号 + AbortController」双保险：过期的请求既被 abort，回来了也因序号不符被丢弃。
 *    ⚠ 只有 abort 不够——请求已经在返回途中时 abort 不一定来得及，序号是兜底。
 *
 * ## 一条纪律
 * **所有页面切换都必须经过 renderView()**，不要在别处出现 `main.innerHTML = ...`。
 * 一旦有第二条渲染路径，加载态和竞态保护就只覆盖其中一条。
 */
import type { Route } from './parse.js';
import type { PageModule } from '../pages/types.js';
import { pageOf } from '../pages/registry.js';
import { $ } from '../shared/dom.js';
import { state } from '../shared/state.js';
import { renderNav } from '../shared/nav.js';
import { doFlash } from '../shared/common.js';
import { setLeaveGuard, setPageHandler } from '../shared/events.js';
import { enhanceSelects } from '../shared/select.js';
import { enhanceDates } from '../shared/datepick.js';
import { endProgress, errorHtml, skeletonHtml, startProgress } from '../shared/feedback.js';
import { errText, isRetryable } from '../api/client.js';

/** 每次渲染 +1；回调回来时序号对不上就说明自己已经过期了 */
let seq = 0;
let inflight: AbortController | null = null;
/** 上一次成功渲染的 route，重试时用 */
let lastRoute: Route | null = null;

export async function renderView(route: Route, opts: { silent?: boolean } = {}): Promise<void> {
  lastRoute = route;
  const my = ++seq;
  inflight?.abort();
  const ac = new AbortController();
  inflight = ac;

  /* 登录页不进两栏栅格：整页替换，侧栏与主区都不渲染。
     body.anon 是两个容器的唯一开关，⚠ 别在别处再加第二个判断（一期原则） */
  document.body.classList.toggle('anon', route.view === 'login');

  const host = route.view === 'login' ? $('#login-root') : $('#main');
  if (!host) return;
  if (route.view !== 'login') renderNav();

  const page = pageOf(route.view) as PageModule<unknown>;

  /* 加载态两形态（2026-08-13 拍板）：
       首次进页（内容区是空的 / 换了页面）→ 骨架，避免大片空白；
       页内刷新（写操作后重拉，silent）  → 顶部进度条 + 旧内容变淡，不丢滚动位置。 */
  const firstPaint = !opts.silent && host.getAttribute('data-view') !== route.view;
  if (firstPaint) {
    host.innerHTML = skeletonHtml(page.skeleton);
    host.setAttribute('data-view', route.view);
  }
  startProgress();

  let data: unknown;
  try {
    data = await page.load({ route, signal: ac.signal });
  } catch (e) {
    // 自己 abort 的：说明用户已经切到别的页了，什么都不做，让新的那次渲染负责
    if (e instanceof DOMException && e.name === 'AbortError') return;
    if (my !== seq) return;
    endProgress();
    host.innerHTML = errorHtml(errText(e), isRetryable(e));
    host.setAttribute('data-view', route.view);
    setPageHandler(null);
    return;
  }

  if (my !== seq) return; // 过期响应，丢弃

  host.innerHTML = page.html(data, route);
  host.setAttribute('data-view', route.view);
  /* ⚠ 同步接管本页的下拉，赶在 mounted 之前：mounted 里有量宽度 / 聚焦的活，
     交给 select.ts 内部那个 MutationObserver（微任务）就晚了一步 */
  enhanceSelects(host);
  enhanceDates(host);
  /* 入场阶梯（`21_motion.css`）只在**首次进页**播一次：排序 / 筛选 / 写操作后的重拉
     都走同一条渲染路径，若无条件播放，每点一次表头整页都要重新淡入一遍——比不做动效还烦。
     ⚠ 挂在 `.page` 上（页面根元素），不是挂在 #main 上：#main 不会被替换，class 会一直留着。 */
  if (firstPaint) host.firstElementChild?.classList.add('mo-in');
  endProgress();

  /* ⚠ 离开拦截每次渲染都先清空，再由页面在 mounted 里按需重设（W2-6）：
     不清的话，从双周报编辑页走掉之后守卫会留在那儿，继续拦别的页面的跳转。 */
  setLeaveGuard(null);
  page.mounted?.(data, route);
  // 把本页的事件分支挂上；⚠ 每次渲染都重挂，闭包里才是最新的 data
  setPageHandler(page.onEvent ? (e: Event) => page.onEvent?.(e, data, route) : null);
  doFlash();
}

/**
 * 重试当前页（错误态的按钮走这里）。
 * ⚠ 重试要走 firstPaint 分支重新给骨架，所以先清掉 data-view 标记。
 */
export function retryCurrent(): void {
  if (!lastRoute) return;
  $('#main')?.removeAttribute('data-view');
  $('#login-root')?.removeAttribute('data-view');
  void renderView(lastRoute);
}

/**
 * 写操作之后就地刷新当前页（不闪骨架、不丢滚动位置）。
 * ⚠ 如果这次写操作动了队伍 / 周期，必须先 `await meta.refresh()` 再调它，
 *   否则路由守卫还拿着旧列表（meta.ts 纪律 2）。
 */
export function refreshCurrent(): void {
  if (!lastRoute) return;
  void renderView(lastRoute, { silent: true });
}

/** 供页面读取：当前视图。⚠ 只读，不要在页面里给它赋值——那是守卫的活 */
export const currentView = (): string => state.view;
